import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import clsx from 'clsx';
import type { SocialPlatform } from '../../types';
import editIcon from '@/assets/images/svg/edit_icon.svg';

interface SocialRowProps {
  platform: SocialPlatform;
  onConnect: (id: string, value: string) => void;
  onDisconnect: (id: string) => void;
}

export const SocialRow: React.FC<SocialRowProps> = ({ platform, onConnect, onDisconnect }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(platform.value || '');

  // Helper to determine display text for Twitter based on viewport
  const getPlatformName = (name: string) => {
    if (name.includes('Twitter')) {
      return (
        <>
          <span className='md:hidden'>X (twitter)</span>
          <span className='hidden md:inline'>X (Former Twitter)</span>
        </>
      );
    }
    return name;
  };

  // 1. Editing State
  if (isEditing) {
    return (
      <div
        className={clsx(
          'mb-3 flex flex-col gap-4 rounded-20 border border-purple-150 bg-white/60 p-4',
          'md:mb-0 md:flex-row md:items-center md:justify-between md:rounded-none md:border-x-0 md:border-b md:border-t-0 md:border-gray-200 md:bg-transparent md:py-3'
        )}
      >
        <div className='flex min-w-0 flex-1 items-center gap-4'>
          <div className='flex size-10 shrink-0 items-center justify-center'>
            <img
              src={platform.icon}
              alt={`${platform.name} icon`}
              className='size-full object-contain'
            />
          </div>
          <div className='w-full md:max-w-[400px]'>
            <Input
              variant='light'
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={`Enter ${platform.name} URL`}
              autoFocus
            />
          </div>
        </div>

        <div className='flex items-center gap-2 self-end md:self-auto'>
          {/* SAVE Button - Same width as Connect (md:w-[154px]) */}
          <Button
            type='button'
            variant='save'
            onClick={() => {
              if (inputValue.trim()) {
                onConnect(platform.id, inputValue);
                setIsEditing(false);
              }
            }}
            className='h-[36px] w-[100px] md:w-[154px]'
          >
            SAVE
          </Button>

          {/* Cancel Button - Text style, matches Connect width */}
          <button
            type='button'
            onClick={() => {
              setIsEditing(false);
              setInputValue(platform.value || '');
            }}
            className={clsx(
              'flex h-[36px] items-center justify-center',
              'w-[100px] md:w-[154px]',
              // Typography: Inter V, SemiBold, 20px, 27px, 0% tracking, Black
              'font-sans text-20 font-semibold leading-27 tracking-normal text-black',
              'transition-opacity hover:opacity-70'
            )}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // 2. Connected State
  if (platform.isConnected) {
    return (
      <div
        className={clsx(
          'mb-3 flex flex-col gap-4 rounded-20 border border-purple-150 bg-white/60 p-4',
          'md:mb-0 md:flex-row md:items-center md:justify-between md:rounded-none md:border-x-0 md:border-b md:border-t-0 md:border-gray-200 md:bg-transparent md:py-3'
        )}
      >
        <div className='flex min-w-0 items-center gap-4'>
          {/* Group Icon and Dot tightly */}
          <div className='flex items-center gap-1.5'>
            <div className='relative flex size-10 shrink-0 items-center justify-center'>
              <img
                src={platform.icon}
                alt={`${platform.name} icon`}
                className='size-full object-contain'
              />
            </div>
            {/* Green Dot - Very close to icon */}
            <div className='size-[6px] shrink-0 rounded-full bg-accent-300' />
          </div>

          <span className='truncate font-sans text-18 font-semibold leading-160 tracking-neg-2 text-brand-800'>
            {platform.value}
          </span>
        </div>

        <div className='flex items-center gap-6 self-end md:self-auto'>
          {/* Edit Button with Icon */}
          <button
            type='button'
            onClick={() => setIsEditing(true)}
            className='flex items-center gap-1 transition-opacity hover:opacity-70'
          >
            <img src={editIcon} alt='Edit' className='size-4' />
            <span className='font-sans text-16 font-bold text-brand-800 underline decoration-solid'>
              Edit
            </span>
          </button>

          {/* DELETE Button - Text only, Red */}
          <button
            type='button'
            onClick={() => {
              onDisconnect(platform.id);
              setInputValue('');
            }}
            className={clsx(
              'flex items-center justify-center',
              // Typography: 16px, Red (#D33C3E)
              'font-sans text-16 font-semibold leading-27 tracking-normal text-delete-input',
              'transition-opacity hover:opacity-80'
            )}
          >
            DELETE
          </button>
        </div>
      </div>
    );
  }

  // 3. Idle State
  return (
    <div
      className={clsx(
        'mb-3 flex flex-row items-center justify-between rounded-20 border border-purple-150 bg-white/60 p-4',
        'md:mb-0 md:rounded-none md:border-x-0 md:border-b md:border-t-0 md:border-gray-200 md:bg-transparent md:py-3'
      )}
    >
      <div className='flex min-w-0 items-center gap-4'>
        <div className='flex size-10 shrink-0 items-center justify-center'>
          <img
            src={platform.icon}
            alt={`${platform.name} icon`}
            className='size-full object-contain'
          />
        </div>

        <div className='flex flex-col'>
          <span className='font-sans text-16 font-bold leading-21 text-brand-800'>
            {getPlatformName(platform.name)}
          </span>
          <span className='font-sans text-12 font-normal leading-[16px] text-gray-500'>
            Not Connected
          </span>
        </div>
      </div>

      <div className='shrink-0'>
        <Button
          type='button'
          variant='settings-modify'
          className={clsx(
            // Responsive Height: 50px (mobile) -> 41px (desktop)
            'h-[3.125rem] md:h-[2.60rem]',
            // Width: auto (mobile) -> 154px (desktop)
            'w-auto px-6 md:w-[154px]',
            'text-16 text-brand-800'
          )}
          onClick={() => setIsEditing(true)}
        >
          Connect
        </Button>
      </div>
    </div>
  );
};
